import { useEffect, useState } from 'react';
import { SlidersHorizontal, RotateCcw, Info } from 'lucide-react';
import {
  TID, AMENITIES, PRICE_RANGE, AREA_RANGE, formatKurus, toKurus, toLira,
  getTierForGuests, type SearchFilters,
} from '@yalidavet/shared';
import DualRangeSlider from './DualRangeSlider';

export interface FilterState {
  minPriceKurus: number;
  maxPriceKurus: number;
  minAreaM2: number;
  maxAreaM2: number;
  hasPool: boolean;
  hasGarden: boolean;
  hasSeaView: boolean;
  hasParking: boolean;
}

export const defaultFilterState = (): FilterState => ({
  minPriceKurus: PRICE_RANGE.min,
  maxPriceKurus: PRICE_RANGE.max,
  minAreaM2: AREA_RANGE.min,
  maxAreaM2: AREA_RANGE.max,
  hasPool: false,
  hasGarden: false,
  hasSeaView: false,
  hasParking: false,
});

interface Props {
  value: FilterState;
  /** Applied only when "Filtreleri Uygula" is pressed. */
  onApply: (next: FilterState) => void;
  onReset: () => void;
  /** Guest count from the active search — shows the garden requirement hint. */
  guestCount?: number;
}

export default function FiltersPanel({ value, onApply, onReset, guestCount }: Props) {
  const [draft, setDraft] = useState<FilterState>(value);
  useEffect(() => setDraft(value), [value]);

  const tier = guestCount ? safeTier(guestCount) : null;

  const toggle = (key: keyof FilterState) =>
    setDraft((d) => ({ ...d, [key]: !d[key] }));

  return (
    <aside
      className="yd-card sticky top-20 h-fit p-5"
      data-testid={TID.filtersPanel}
      aria-label="Filtreler"
    >
      <div className="mb-5 flex items-center justify-between">
        <h2 className="flex items-center gap-2 font-semibold text-ink-900">
          <SlidersHorizontal className="h-4 w-4" aria-hidden />
          Filtreler
        </h2>
        <button
          type="button"
          onClick={onReset}
          className="flex items-center gap-1 text-xs font-medium text-ink-500 hover:text-ink-800"
          data-testid={TID.filterReset}
        >
          <RotateCcw className="h-3.5 w-3.5" aria-hidden /> Sıfırla
        </button>
      </div>

      {/* Garden requirement hint driven by the capacity tier */}
      {tier && (
        <p
          className="mb-5 flex gap-2 rounded-lg bg-bosphorus-50 p-3 text-xs text-bosphorus-900"
          data-testid="filters-tier-hint"
          data-required-garden={tier.minGardenM2}
          data-tier={tier.tier}
        >
          <Info className="h-4 w-4 shrink-0" aria-hidden />
          <span>
            <strong>{guestCount} misafir</strong> için Tier {tier.tier} kuralları geçerli:
            en az <strong>{tier.minGardenM2} m²</strong> bahçeli mülkler listelenir.
          </span>
        </p>
      )}

      <div className="space-y-6">
        {/* Fiyat Aralığı */}
        <DualRangeSlider
          label="Fiyat Aralığı"
          min={PRICE_RANGE.min}
          max={PRICE_RANGE.max}
          step={PRICE_RANGE.step}
          value={[draft.minPriceKurus, draft.maxPriceKurus]}
          onChange={([lo, hi]) => setDraft((d) => ({ ...d, minPriceKurus: lo, maxPriceKurus: hi }))}
          format={(v) => formatKurus(v)}
          parseInput={(raw) => toKurus(Number(raw))}
          toInput={(v) => toLira(v)}
          suffix="₺"
          minTestId={TID.filterPriceMin}
          maxTestId={TID.filterPriceMax}
          minSliderTestId={TID.filterPriceSliderMin}
          maxSliderTestId={TID.filterPriceSliderMax}
        />

        {/* Metrekare */}
        <DualRangeSlider
          label="Metrekare (m²)"
          min={AREA_RANGE.min}
          max={AREA_RANGE.max}
          step={AREA_RANGE.step}
          value={[draft.minAreaM2, draft.maxAreaM2]}
          onChange={([lo, hi]) => setDraft((d) => ({ ...d, minAreaM2: lo, maxAreaM2: hi }))}
          format={(v) => `${new Intl.NumberFormat('tr-TR').format(v)} m²`}
          suffix="m²"
          minTestId={TID.filterAreaMin}
          maxTestId={TID.filterAreaMax}
          minSliderTestId={TID.filterAreaSliderMin}
          maxSliderTestId={TID.filterAreaSliderMax}
        />

        {/* Özellikler */}
        <fieldset>
          <legend className="yd-label">Özellikler</legend>
          <div className="space-y-2.5">
            {AMENITIES.map((a) => (
              <label key={a.key} className="flex cursor-pointer items-center gap-2.5 text-sm text-ink-700">
                <input
                  type="checkbox"
                  className="h-4 w-4 rounded border-ink-300 text-bosphorus-600 focus:ring-bosphorus-500"
                  checked={draft[a.key as keyof FilterState] as boolean}
                  onChange={() => toggle(a.key as keyof FilterState)}
                  data-testid={TID.filterAmenity(a.testKey)}
                />
                {a.label}
              </label>
            ))}
          </div>
        </fieldset>

        <button
          type="button"
          onClick={() => onApply(draft)}
          className="yd-btn-primary w-full"
          data-testid={TID.filterApply}
        >
          Filtreleri Uygula
        </button>
      </div>
    </aside>
  );
}

function safeTier(guestCount: number) {
  try {
    return getTierForGuests(guestCount);
  } catch {
    return null;
  }
}

/** FilterState → API query params (omits untouched defaults). */
export function filterStateToParams(f: FilterState): Partial<SearchFilters> {
  const out: Partial<SearchFilters> = {};
  if (f.minPriceKurus > PRICE_RANGE.min) out.minPriceKurus = f.minPriceKurus;
  if (f.maxPriceKurus < PRICE_RANGE.max) out.maxPriceKurus = f.maxPriceKurus;
  if (f.minAreaM2 > AREA_RANGE.min) out.minAreaM2 = f.minAreaM2;
  if (f.maxAreaM2 < AREA_RANGE.max) out.maxAreaM2 = f.maxAreaM2;
  if (f.hasPool) out.hasPool = true;
  if (f.hasGarden) out.hasGarden = true;
  if (f.hasSeaView) out.hasSeaView = true;
  if (f.hasParking) out.hasParking = true;
  return out;
}
